import React, { useEffect, useMemo, use } from 'react';
import EventEmitter from 'eventemitter3';
import { useDispatch } from 'react-redux';
import { cacheReady } from './redux/actions';
import ddt from '@wix/da-ddt';
const logger = ddt.createLogger('rwait');
// Setup EE asap and singleton
const ee = new EventEmitter();
const signalCache = {};
/**
 * The is the Resource Waiter, it uses the new use() hook react provides to
 * suspend a component until the resource cache is ready.
 *
 * Note this cannot be included in SSR content, must wait for mount.
 */ const RWait = /*#__PURE__*/ React.memo(({ cacheName, skipCache })=>{
    const dispatch = useDispatch();
    if (typeof window === 'undefined') {
        throw new Error('You cannot use RWait in SSR, you will make the server wait forever');
    }
    useEffect(()=>{
        var _window___REE__;
        if (typeof window !== 'undefined' && !((_window___REE__ = window.__REE__) === null || _window___REE__ === void 0 ? void 0 : _window___REE__.on)) {
            window.__REE__ = ee;
        }
    }, []);
    const stablePromise = useMemo(()=>{
        return new Promise((r)=>{
            var _window___RCACHE__, // Listen for cache ready
            _window___REE__, _window___REE__1;
            logger.log('Starting RWaiter');
            // Allow consumer to skip the cache when they want
            if (skipCache) {
                logger.log('Skipped cache', cacheName);
                return r(true);
            }
            // When its in the cache resolve fast
            if ((_window___RCACHE__ = window.__RCACHE__) === null || _window___RCACHE__ === void 0 ? void 0 : _window___RCACHE__[cacheName]) {
                logger.log('Cache is warm resolving suspense promise', cacheName);
                return r(window.__RCACHE__[cacheName]);
            }
            ((_window___REE__ = window.__REE__) === null || _window___REE__ === void 0 ? void 0 : _window___REE__.on) && ((_window___REE__1 = window.__REE__) === null || _window___REE__1 === void 0 ? void 0 : _window___REE__1.on('cacheReady', (name)=>{
                if (name === cacheName) {
                    var _window___RCACHE__;
                    logger.log('Got cacheReady event, resolving suspense promise', cacheName);
                    r((_window___RCACHE__ = window.__RCACHE__) === null || _window___RCACHE__ === void 0 ? void 0 : _window___RCACHE__[cacheName]);
                }
            }));
            // Flush any queued events for this resource
            // Replay any events sent before EE was available
            // Note we assume one resource per cache
            if (window.__REER__) {
                const replay = window.__REER__;
                if (Array.isArray(replay) && replay.length > 0) {
                    replay.forEach((e, i)=>{
                        if (e && e.d === cacheName) {
                            logger.log('Flushing event to EE', cacheName, e);
                            window.__REE__.emit(e.n, e.d);
                            window.__REER__[i] = undefined; // clear queue
                        }
                    });
                }
            }
        });
    }, [
        cacheName,
        skipCache
    ]);
    // The new magical use hook...
    const ready = use(stablePromise);
    // We dispatch to redux with the cache data, but only once
    // not ref didn't work here, use() isn't like other hooks so I guess that is why
    if (ready && !signalCache[cacheName] && !skipCache) {
        logger.log('Cache is fetched updating redux', cacheName);
        signalCache[cacheName] = true;
        performance.mark(`RCacheDone:${cacheName}`);
        performance.measure(`RWait Redux ${cacheName}': HEAD`, 'RCacheHead', `RCacheDone:${cacheName}`);
        performance.measure(`RWait Redux ${cacheName}': Ready`, 'RCacheReady', `RCacheDone:${cacheName}`);
        performance.measure(`RWait Redux ${cacheName}': App`, 'RCacheAppStartup', `RCacheDone:${cacheName}`);
        // Schedule for next tick, this seems to fix some inf. loops with this and measured cookie doing
        // something odd with redux.
        setTimeout(()=>{
            var _window___RCACHE__;
            return dispatch(cacheReady(cacheName, (_window___RCACHE__ = window.__RCACHE__) === null || _window___RCACHE__ === void 0 ? void 0 : _window___RCACHE__[cacheName]));
        }, 0);
    }
    return null;
});
RWait.displayName = 'RWait';
export default RWait;
